import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Clapperboard,
  FileText,
  Headphones,
  Palette,
  Sparkles,
} from 'lucide-react';

import PixelBackground from '@/components/PixelBackground';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import Seo from '@/components/Seo';
import { JsonLd } from '@/components/JsonLd';
import Display from '@/components/typography/Display';
import Button from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Container, HeroSection, Section } from '@/components/ui/Section';
import { useLanguage } from '@/contexts/LanguageContext';
import { useLocalizedPath, stripLanguagePrefix, withLanguagePrefix } from '@/lib/locale';
import { BASE_URL, getHreflangLinks } from '@/seo/meta';
import {
  generateBreadcrumbSchema,
  generateFAQSchema,
  generateOrganizationSchema,
  generateServiceSchema,
  generateWebsiteSchema,
} from '@/lib/seo';
import { trackCTA, trackServiceView } from '@/lib/analytics';
import { trackScroll } from '@/lib/scroll';
import { getServiceMeta } from '@/seo/meta';

const serviceName = 'Παραγωγή Περιεχομένου';
const serviceSlug = 'content-production';

type Locale = 'en' | 'el';

type HeroContent = {
  eyebrow: string;
  title: string;
  description: string;
  bullets: string[];
  primaryCta: string;
  secondaryCta: string;
};

type SectionCopy = {
  eyebrow?: string;
  heading: string;
  description: string;
};

type MeaningContent = SectionCopy & {
  rightTitle: string;
  bullets: string[];
  note: string;
};

type CapabilityGroup = {
  title: string;
  description: string;
  bullets: string[];
};

type ProcessStep = {
  title: string;
  description: string;
  bullets: string[];
};

type DeliverablesContent = {
  eyebrow: string;
  heading: string;
  description: string;
  bullets: string[];
  note: string;
};

type FaqItem = {
  question: string;
  answer: string;
};

type ClosingContent = {
  eyebrow?: string;
  title: string;
  description: string;
  bullets: string[];
  primaryCta: string;
};

const heroCopy: Record<Locale, HeroContent> = {
  en: {
    eyebrow: 'Content production',
    title: 'Content that shows who you are and why people should trust you.',
    description:
      "Content is the first thing your customer sees. If it isn't clear, correct, and consistent, the opportunity is lost.",
    bullets: [
      'Explains what you do',
      'Shows professionalism',
      'Supports sales and communication',
    ],
    primaryCta: 'Book a free consultation',
    secondaryCta: 'See projects',
  },
  el: {
    eyebrow: 'Παραγωγή Περιεχομένου',
    title: 'Περιεχόμενο που δείχνει ποιοι είστε και γιατί να σας εμπιστευτούν.',
    description:
      'Το περιεχόμενο είναι αυτό που βλέπει πρώτα ο πελάτης σας. Αν δεν είναι ξεκάθαρο, σωστό και συνεπές, χάνεται η ευκαιρία.',
    bullets: [
      'εξηγεί τι κάνετε',
      'δείχνει επαγγελματισμό',
      'υποστηρίζει πωλήσεις και επικοινωνία',
    ],
    primaryCta: 'Κλείστε δωρεάν συμβουλευτική',
    secondaryCta: 'Δείτε έργα',
  },
};

const meaningCopy: Record<Locale, MeaningContent> = {
  en: {
    eyebrow: 'What we mean by “content”',
    heading: 'What we mean by “content”',
    description: "We don't just mean posts.",
    rightTitle: 'We mean:',
    bullets: ['Images', 'Videos', 'Copy', 'Graphics'],
    note: 'With a clear purpose and working together, not in fragments.',
  },
  el: {
    eyebrow: 'Τι εννοούμε «περιεχόμενο»',
    heading: 'Τι εννοούμε «περιεχόμενο»',
    description: 'Δεν μιλάμε απλώς για posts.',
    rightTitle: 'Μιλάμε για:',
    bullets: ['εικόνες', 'βίντεο', 'κείμενα', 'γραφικά'],
    note: 'που έχουν συγκεκριμένο σκοπό και δουλεύουν μαζί, όχι αποσπασματικά.',
  },
};

const capabilitiesCopy: Record<Locale, { eyebrow: string; heading: string; description: string; groups: CapabilityGroup[] }> = {
  en: {
    eyebrow: 'What we handle',
    heading: 'What we handle',
    description: 'Clear scope for everything you need to show up consistently.',
    groups: [
      {
        title: 'Video',
        description: 'Video production for:',
        bullets: [
          'Social media',
          'Websites',
          'Ads',
          'From short clips to complete presentations',
        ],
      },
      {
        title: 'Photography',
        description: 'We photograph:',
        bullets: [
          'Products',
          'Spaces',
          'Teams and professionals',
          'For use on site, social, and print',
        ],
      },
      {
        title: 'Visuals for social',
        description: 'We design:',
        bullets: [
          'Consistent style',
          'Templates',
          'Post graphics',
          'So every post feels aligned',
        ],
      },
      {
        title: 'Copy',
        description: 'We write copy for:',
        bullets: [
          'Social media',
          'Websites',
          'Presentations or campaigns',
          'Simple, clear, without exaggeration',
        ],
      },
      {
        title: 'Content organization',
        description: 'We help establish:',
        bullets: [
          'Plan',
          'Cadence',
          'Consistency',
          'So you know what goes live, when, and why',
        ],
      },
      {
        title: 'Audio',
        description: 'Audio production for:',
        bullets: [
          'Radio spots',
          'Ad videos',
          'Voice-over and intro/outro',
          'Music tailored to the brand',
        ],
      },
    ],
  },
  el: {
    eyebrow: 'Τι αναλαμβάνουμε',
    heading: 'Τι αναλαμβάνουμε',
    description: 'Συγκεκριμένο scope για ό,τι χρειάζεται να δείχνει σωστά.',
    groups: [
      {
        title: 'Βίντεο',
        description: 'Παραγωγή βίντεο για:',
        bullets: [
          'social media',
          'ιστοσελίδες',
          'διαφημίσεις',
          'Από απλά σύντομα βίντεο μέχρι πιο ολοκληρωμένες παρουσιάσεις.',
        ],
      },
      {
        title: 'Φωτογραφία',
        description: 'Φωτογραφίζουμε:',
        bullets: [
          'προϊόντα',
          'χώρους',
          'ομάδες και επαγγελματίες',
          'Με εικόνες που μπορούν να χρησιμοποιηθούν σε site, social και έντυπα.',
        ],
      },
      {
        title: 'Οπτικό περιεχόμενο για social',
        description: 'Σχεδιάζουμε:',
        bullets: [
          'σταθερό στυλ',
          'templates',
          'γραφικά για αναρτήσεις',
          'Για να υπάρχει συνέπεια και όχι «κάθε post και άλλο ύφος».',
        ],
      },
      {
        title: 'Κείμενα',
        description: 'Γράφουμε κείμενα:',
        bullets: [
          'για social media',
          'για ιστοσελίδες',
          'για παρουσιάσεις ή καμπάνιες',
          'Απλά, κατανοητά και χωρίς υπερβολές.',
        ],
      },
      {
        title: 'Οργάνωση περιεχομένου',
        description: 'Βοηθάμε να υπάρχει:',
        bullets: [
          'πλάνο',
          'ρυθμός',
          'συνοχή',
          'ώστε να ξέρετε τι ανεβαίνει, πότε και γιατί.',
        ],
      },
      {
        title: 'Audio',
        description: 'Παραγωγή ήχου για:',
        bullets: [
          'ραδιοφωνικά spots',
          'διαφημιστικά βίντεο',
          'voice-over και intro/outro',
          'μουσική προσαρμοσμένη στο brand',
        ],
      },
    ],
  },
};

const processIntroCopy: Record<Locale, SectionCopy> = {
  en: {
    eyebrow: 'How we work',
    heading: 'How we work',
    description: 'A simple and clear workflow.',
  },
  el: {
    eyebrow: 'Πώς δουλεύουμε',
    heading: 'Πώς δουλεύουμε',
    description: 'Ένα απλό και ξεκάθαρο workflow',
  },
};

const processCopy: Record<Locale, ProcessStep[]> = {
  en: [
    {
      title: 'Understanding',
      description: 'We learn:',
      bullets: ['Your business', 'Your audience', 'What you want to communicate'],
    },
    {
      title: 'Planning',
      description: 'We define:',
      bullets: ['Content type', 'Tone', 'Core themes'],
    },
    {
      title: 'Production',
      description: 'We create:',
      bullets: ['Photos', 'Videos', 'Graphics', 'Copy'],
    },
    {
      title: 'Delivery',
      description: 'We provide:',
      bullets: ['Ready assets', 'Files for future use', 'Guidelines if needed'],
    },
  ],
  el: [
    {
      title: 'Κατανόηση',
      description: 'Μαθαίνουμε:',
      bullets: ['την επιχείρησή σας', 'το κοινό σας', 'τι θέλετε να επικοινωνήσετε'],
    },
    {
      title: 'Σχεδιασμός',
      description: 'Ορίζουμε:',
      bullets: ['είδος περιεχομένου', 'ύφος', 'βασικές θεματικές'],
    },
    {
      title: 'Παραγωγή',
      description: 'Δημιουργούμε:',
      bullets: ['φωτογραφίες', 'βίντεο', 'γραφικά', 'κείμενα'],
    },
    {
      title: 'Παράδοση',
      description: 'Σας δίνουμε:',
      bullets: ['έτοιμο υλικό', 'αρχεία για μελλοντική χρήση', 'οδηγίες αν χρειάζονται'],
    },
  ],
};

const deliverablesCopy: Record<Locale, DeliverablesContent> = {
  en: {
    eyebrow: 'Deliverables',
    heading: 'What we deliver',
    description: 'Depending on the project, we deliver:',
    bullets: [
      'Photo and video files',
      'Graphics and templates',
      'Copy',
      'Organized assets ready to use',
    ],
    note: 'No lock-ins or dependencies.',
  },
  el: {
    eyebrow: 'Τι παραδίδουμε',
    heading: 'Τι παραδίδουμε',
    description: 'Ανάλογα το project, παραδίδουμε:',
    bullets: [
      'αρχεία φωτογραφίας και βίντεο',
      'γραφικά και templates',
      'κείμενα',
      'οργανωμένο υλικό για άμεση χρήση',
    ],
    note: 'Χωρίς «κλειδώματα» ή εξαρτήσεις.',
  },
};

const faqIntroCopy: Record<Locale, SectionCopy> = {
  en: {
    eyebrow: 'FAQ',
    heading: 'Frequently asked questions',
    description: 'Everything you need to know before starting.',
  },
  el: {
    eyebrow: 'Συχνές Ερωτήσεις',
    heading: 'Συχνές Ερωτήσεις',
    description: 'Όσα χρειάζεται να γνωρίζετε πριν ξεκινήσετε.',
  },
};

const faqCopy: Record<Locale, FaqItem[]> = {
  en: [
    {
      question: 'Do you also do video?',
      answer: 'Yes. From simple social videos to more complete productions.',
    },
    {
      question: 'How often can content be created?',
      answer: 'Depending on your needs. It can be a project or an ongoing collaboration.',
    },
    {
      question: 'Do you also manage social media?',
      answer: 'We can help with content and structure. Management is discussed separately.',
    },
    {
      question: 'Can I use the assets elsewhere?',
      answer: 'Yes. The assets are yours and can be used wherever you want.',
    },
  ],
  el: [
    {
      question: 'Κάνετε και βίντεο;',
      answer: 'Ναι. Από απλά social βίντεο μέχρι πιο ολοκλημένες παραγωγές.',
    },
    {
      question: 'Πόσο συχνά μπορεί να δημιουργείται περιεχόμενο;',
      answer: 'Ανάλογα με τις ανάγκες σας. Μπορεί να είναι project ή συνεχής συνεργασία.',
    },
    {
      question: 'Αναλαμβάνετε και social media;',
      answer: 'Μπορούμε να βοηθήσουμε στο περιεχόμενο και στη δομή. Η διαχείριση συζητιέται ξεχωριστά.',
    },
    {
      question: 'Θα μπορώ να χρησιμοποιώ το υλικό και αλλού;',
      answer: 'Ναι. Το υλικό είναι δικό σας και μπορεί να χρησιμοποιηθεί όπου θέλετε.',
    },
  ],
};

const closingCopy: Record<Locale, ClosingContent> = {
  en: {
    eyebrow: 'Next step',
    title: 'Want content that represents you correctly?',
    description: "Let's see:",
    bullets: [
      'What you truly need',
      'What makes sense to create',
      'How to use it properly',
    ],
    primaryCta: 'Book a free consultation',
  },
  el: {
    eyebrow: 'Επόμενο βήμα',
    title: 'Θέλετε περιεχόμενο που να σας εκπροσωπεί σωστά;',
    description: 'Ας δούμε:',
    bullets: [
      'τι χρειάζεστε πραγματικά',
      'τι έχει νόημα να δημιουργηθεί',
      'πώς να αξιοποιηθεί σωστά',
    ],
    primaryCta: 'Κλείστε δωρεάν συμβουλευτική',
  },
};

const capabilityIcons: LucideIcon[] = [Clapperboard, Palette, Sparkles, FileText, Calendar, Headphones];

export default function ContentProduction() {
  const { language } = useLanguage();
  const location = useLocation();
  const locale: Locale = language === 'el' ? 'el' : 'en';
  const localizedPath = useLocalizedPath();

  const metaData = getServiceMeta(serviceSlug, locale);
  const canonicalPath = withLanguagePrefix(stripLanguagePrefix(location.pathname), locale);
  const canonicalUrl = `${BASE_URL}${canonicalPath}`;
  const hreflang = getHreflangLinks(location.pathname, locale);
  const serviceDisplayName = locale === 'en' ? 'Content Production' : serviceName;

  const hero = heroCopy[locale];
  const meaning = meaningCopy[locale];
  const capabilities = capabilitiesCopy[locale];
  const processIntro = processIntroCopy[locale];
  const processSteps = processCopy[locale];
  const deliverables = deliverablesCopy[locale];
  const faqIntro = faqIntroCopy[locale];
  const faqItems = faqCopy[locale];
  const closing = closingCopy[locale];

  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  useEffect(() => {
    trackServiceView(serviceSlug, language);
    const cleanup = trackScroll({ threshold: 0.75, once: true });
    return cleanup;
  }, [language]);

  const handleCTA = (ctaText: string) => {
    trackCTA(ctaText, window.location.pathname);
    window.navigateToConsultation?.();
  };

  const toggleFaq = (index: number) => {
    setOpenFaqIndex((current) => (current === index ? null : index));
  };

  return (
    <>
      <Seo
        title={metaData.title}
        description={metaData.description}
        url={canonicalUrl}
        image={metaData.image}
        hreflang={hreflang}
      />

      <JsonLd json={generateOrganizationSchema()} />
      <JsonLd json={generateWebsiteSchema(locale)} />
      <JsonLd json={generateBreadcrumbSchema(serviceDisplayName, serviceSlug, locale)} />
      <JsonLd json={generateServiceSchema(serviceDisplayName, metaData.description, canonicalUrl, 'Content Production')} />
      <JsonLd json={generateFAQSchema(faqItems)} />

      <div className="relative min-h-screen bg-[#0b0a0e] text-white">
        <PixelBackground />
        <Header variant="simple" />

        <main id="main-content" className="relative z-10 overflow-hidden">
          <HeroSection className="relative overflow-hidden pt-32 pb-20 md:pt-36 lg:pt-40 lg:pb-24">
            <div className="absolute inset-0 geometric-pattern" aria-hidden="true" />
            <div
              className="absolute inset-0 bg-gradient-to-br from-pp-black/95 via-black/85 to-black"
              aria-hidden="true"
            />
            <Container className="relative lg:min-h-[calc(100vh-220px)] lg:flex lg:flex-col lg:justify-center">
              <div className="max-w-3xl space-y-8">
                <div className="space-y-5">
                  <p className="text-[11px] font-medium uppercase tracking-[0.4em] text-brand-highlight/70">
                    {hero.eyebrow}
                  </p>
                  <Display
                    variant="h1"
                    as="h1"
                    className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.12] tracking-tight"
                  >
                    {hero.title}
                  </Display>
                </div>

                <Display
                  variant="subhead"
                  as="p"
                  className="max-w-2xl text-base sm:text-lg leading-relaxed text-white/70"
                >
                  {hero.description}
                </Display>

                <ul className="space-y-3 text-sm text-white/75">
                  {hero.bullets.map((bullet) => (
                    <li key={bullet} className="flex items-start gap-3">
                      <CheckCircle className="mt-0.5 h-4 w-4 text-brand-highlight/70" />
                      <span className="leading-relaxed">{bullet}</span>
                    </li>
                  ))}
                </ul>

                <div className="flex flex-col gap-4 sm:flex-row">
                  <Button
                    variant="primary"
                    size="lg"
                    className="w-full justify-center sm:w-auto"
                    onClick={() => handleCTA(hero.primaryCta)}
                  >
                    {hero.primaryCta}
                  </Button>
                  <Button
                    as="a"
                    href={localizedPath('/projects')}
                    variant="secondary"
                    size="lg"
                    className="w-full justify-center sm:w-auto"
                  >
                    {hero.secondaryCta}
                  </Button>
                </div>
              </div>
            </Container>
          </HeroSection>

          <Section>
            <Container>
              <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_1px_minmax(0,1.05fr)] lg:items-start lg:gap-12">
                <div className="space-y-4">
                  {meaning.eyebrow ? (
                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                      {meaning.eyebrow}
                    </p>
                  ) : null}
                  <h2 className="text-3xl font-semibold text-white md:text-5xl">{meaning.heading}</h2>
                  <p className="text-lg text-white/80">{meaning.description}</p>
                </div>

                <div className="hidden lg:block h-full w-px bg-gradient-to-b from-transparent via-white/10 to-transparent" />

                <Card className="relative overflow-hidden rounded-[28px] border-white/15 bg-black/70 backdrop-blur-xl p-6 sm:p-7 shadow-glass">
                  <div className="pointer-events-none absolute -right-24 -top-20 h-44 w-44 rounded-full bg-brand-highlight/15 blur-3xl" />
                  <div className="pointer-events-none absolute -bottom-24 -left-16 h-40 w-40 rounded-full bg-white/10 blur-3xl" />
                  <div className="relative space-y-6">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-white/50">
                      {meaning.rightTitle}
                    </p>
                    <ul className="space-y-4 text-sm text-white/70">
                      {meaning.bullets.map((bullet) => (
                        <li key={bullet} className="flex items-start gap-3">
                          <span className="mt-2 h-2 w-2 rounded-full bg-brand-highlight/80" />
                          <span className="leading-relaxed">{bullet}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-white/55">{meaning.note}</p>
                  </div>
                </Card>
              </div>
            </Container>
          </Section>

          <Section>
            <Container>
              <div className="max-w-3xl space-y-4">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                  {capabilities.eyebrow}
                </p>
                <h2 className="text-3xl font-semibold text-white md:text-5xl">{capabilities.heading}</h2>
                <p className="text-lg text-white/70">{capabilities.description}</p>
              </div>

              <div className="mt-12 grid gap-8 md:grid-cols-2">
                {capabilities.groups.map((group, index) => {
                  const Icon = capabilityIcons[index] ?? FileText;
                  return (
                    <Card
                      key={group.title}
                      className="group space-y-4 rounded-2xl border-white/15 bg-black/70 backdrop-blur-xl p-8 shadow-glass transition hover:bg-white/10 hover:border-white/25 hover:shadow-lg hover:shadow-white/10"
                    >
                      <div className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5 text-brand-highlight/80 transition group-hover:border-brand-highlight/60 group-hover:text-brand-highlight">
                        <Icon className="h-6 w-6" />
                      </div>
                      <h3 className="text-2xl font-semibold text-white">{group.title}</h3>
                      <p className="text-sm text-white/70">{group.description}</p>
                      <ul className="space-y-3 text-sm text-white/75">
                        {group.bullets.map((bullet) => (
                          <li key={bullet} className="flex items-start gap-3">
                            <CheckCircle className="mt-0.5 h-4 w-4 text-brand-highlight/70" />
                            <span className="leading-relaxed">{bullet}</span>
                          </li>
                        ))}
                      </ul>
                    </Card>
                  );
                })}
              </div>
            </Container>
          </Section>

          <Section id="process" className="relative isolate">
            <div className="absolute inset-0 geometric-pattern opacity-20" aria-hidden="true" />
            <Container className="relative">
              <div className="max-w-3xl space-y-4 text-center md:mx-auto">
                {processIntro.eyebrow ? (
                  <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                    {processIntro.eyebrow}
                  </p>
                ) : null}
                <h2 className="text-3xl font-semibold text-white md:text-5xl">{processIntro.heading}</h2>
                <p className="text-lg text-white/70">{processIntro.description}</p>
              </div>

              <div className="mt-16 grid gap-8 lg:grid-cols-2">
                {processSteps.map((step, index) => (
                  <div
                    key={step.title}
                    className="group flex h-full flex-col gap-4 rounded-2xl border border-white/15 bg-black/70 backdrop-blur-xl p-8 shadow-glass transition hover:bg-white/10 hover:border-white/25 hover:shadow-lg hover:shadow-white/10"
                  >
                    <div className="flex items-center gap-4">
                      <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5 text-lg font-semibold text-white transition group-hover:border-brand-highlight/60 group-hover:text-brand-highlight">
                        {(index + 1).toString().padStart(2, '0')}
                      </span>
                      <h3 className="text-2xl font-semibold text-white">{step.title}</h3>
                    </div>
                    <p className="text-sm text-white/70">{step.description}</p>
                    <ul className="mt-4 space-y-2 text-sm text-white/70">
                      {step.bullets.map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <span className="mt-1 h-1.5 w-1.5 rounded-full bg-brand-highlight/70" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </Container>
          </Section>

          <Section>
            <Container>
              <Card className="relative overflow-hidden rounded-[28px] border-white/15 bg-black/70 backdrop-blur-xl p-8 shadow-glass">
                <div className="pointer-events-none absolute -right-24 -top-20 h-44 w-44 rounded-full bg-brand-highlight/15 blur-3xl" />
                <div className="relative space-y-6">
                  <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                    {deliverables.eyebrow}
                  </p>
                  <h2 className="text-3xl font-semibold text-white md:text-5xl">{deliverables.heading}</h2>
                  <p className="text-lg text-white/70">{deliverables.description}</p>
                  <ul className="space-y-3 text-sm text-white/75">
                    {deliverables.bullets.map((bullet) => (
                      <li key={bullet} className="flex items-start gap-3">
                        <CheckCircle className="mt-0.5 h-4 w-4 text-brand-highlight/70" />
                        <span className="leading-relaxed">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm text-white/55">{deliverables.note}</p>
                </div>
              </Card>
            </Container>
          </Section>

          <Section>
            <Container className="max-w-5xl">
              <div className="text-center">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                  {faqIntro.eyebrow}
                </p>
                <h2 className="mt-4 text-3xl font-semibold text-white md:text-5xl">{faqIntro.heading}</h2>
                <p className="mt-4 text-lg text-white/70">{faqIntro.description}</p>
              </div>

              <div className="mt-12 space-y-4">
                {faqItems.map((faq, index) => {
                  const open = openFaqIndex === index;
                  return (
                    <div
                      key={faq.question}
                      className="overflow-hidden rounded-2xl border border-white/15 bg-black/70 backdrop-blur-xl transition hover:bg-white/10 hover:border-white/25 hover:shadow-lg hover:shadow-white/10"
                    >
                      <button
                        type="button"
                        onClick={() => toggleFaq(index)}
                        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left text-base font-semibold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/40"
                      >
                        <span>{faq.question}</span>
                        {open ? (
                          <ChevronUp className="h-5 w-5 text-white/60" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-white/60" />
                        )}
                      </button>
                      {open && (
                        <div className="border-t border-white/10 px-6 py-5 text-sm text-white/70">
                          <p className="leading-relaxed">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Container>
          </Section>

          <Section className="pt-0">
            <Container>
              <div className="relative overflow-hidden rounded-[32px] border border-white/15 bg-black/70 p-12 shadow-glass">
                <div className="pointer-events-none absolute -right-24 -top-32 h-72 w-72 rounded-full bg-brand-highlight/30 blur-3xl opacity-60" />
                <div className="pointer-events-none absolute -left-24 bottom-0 h-64 w-64 rounded-full bg-white/10 blur-3xl opacity-60" />
                <div className="relative text-center">
                  {closing.eyebrow ? (
                    <p className="text-xs font-semibold uppercase tracking-[0.35em] text-brand-highlight/80">
                      {closing.eyebrow}
                    </p>
                  ) : null}
                  <Display
                    variant="h1"
                    as="h2"
                    className="mx-auto mt-4 max-w-4xl text-balance text-xl font-semibold text-white md:text-3xl"
                  >
                    {closing.title}
                  </Display>
                  <p className="mx-auto mt-4 max-w-3xl text-center text-lg text-white/70">
                    {closing.description}
                  </p>
                  <ul className="mx-auto mt-6 grid max-w-3xl gap-3 text-left text-sm text-white/70 sm:grid-cols-3 sm:gap-4">
                    {closing.bullets.map((bullet) => (
                      <li
                        key={bullet}
                        className="group flex items-start gap-3 rounded-2xl border border-white/5 bg-black/60 px-5 py-4 transition hover:border-white/15"
                      >
                        <span className="mt-1 h-2 w-2 rounded-full bg-brand-highlight/70 transition-colors duration-300 group-hover:bg-brand-highlight" />
                        <span className="leading-relaxed">{bullet}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-10 flex flex-col gap-4 text-center sm:flex-row sm:justify-center">
                    <Button
                      variant="primary"
                      size="lg"
                      className="w-full justify-center sm:w-auto"
                      onClick={() => handleCTA(closing.primaryCta)}
                    >
                      {closing.primaryCta}
                    </Button>
                  </div>
                </div>
              </div>
            </Container>
          </Section>
        </main>

        <Footer variant="compact" />
      </div>
    </>
  );
}
